import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';
import {
  Image,
  Type,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link,
  Code,
  Quote,
  Undo,
  Redo,
  Eye,
  EyeOff,
  Smartphone,
  Tablet,
  Monitor,
  Plus,
  X,
  Upload
} from 'lucide-react';

// Import Quill modules
const ImageResize = require('quill-image-resize-module-react').default;
const { ImageDrop } = require('quill-image-drop-module');

// Register Quill modules
Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/imageDrop', ImageDrop);

// Template variables for email templates
const TEMPLATE_VARIABLES = [
  { name: 'client_name', label: 'Client Name', category: 'Client Info' },
  { name: 'client_email', label: 'Client Email', category: 'Client Info' },
  { name: 'client_phone', label: 'Client Phone', category: 'Client Info' },
  { name: 'client_address', label: 'Client Address', category: 'Client Info' },
  { name: 'account_number', label: 'Account Number', category: 'Account Info' },
  { name: 'case_number', label: 'Case Number', category: 'Account Info' },
  { name: 'company_name', label: 'Company Name', category: 'Company Info' },
  { name: 'agent_name', label: 'Agent Name', category: 'Company Info' },
  { name: 'agent_email', label: 'Agent Email', category: 'Company Info' },
  { name: 'agent_phone', label: 'Agent Phone', category: 'Company Info' },
  { name: 'current_date', label: 'Current Date', category: 'System' },
  { name: 'current_time', label: 'Current Time', category: 'System' },
  { name: 'due_date', label: 'Due Date', category: 'System' },
  { name: 'credit_score', label: 'Credit Score', category: 'Credit Info' },
  { name: 'payment_amount', label: 'Payment Amount', category: 'Financial' },
  { name: 'balance_due', label: 'Balance Due', category: 'Financial' }
];

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  onImageUpload?: (file: File) => Promise<string>;
  className?: string;
  id?: string; // Unique ID for toolbar (auto-generated if not provided)
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing your email content...',
  height = 400,
  onImageUpload,
  className = '',
  id
}) => {
  // Generate unique ID for toolbar if not provided - use useState to ensure it's stable
  const [toolbarId] = useState(() => id || `toolbar-${Math.random().toString(36).substr(2, 9)}`);
  const quillRef = useRef<ReactQuill>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [showVariablePanel, setShowVariablePanel] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Custom image handler for toolbar
  const imageHandler = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle file selection from toolbar
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await handleImageFiles(files);
    if (e.target) {
      e.target.value = '';
    }
  }, []);

  // Handle image file uploads
  const handleImageFiles = useCallback(async (files: File[]) => {
    if (!onImageUpload) return;

    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    for (const file of imageFiles) {
      const uploadId = Math.random().toString(36);
      setUploadingImages(prev => new Set(prev).add(uploadId));

      try {
        const imageUrl = await onImageUpload(file);

        // Insert image into editor
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1, 0);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        // Could show error notification here
      } finally {
        setUploadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(uploadId);
          return newSet;
        });
      }
    }
  }, [onImageUpload]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    await handleImageFiles(files);
  }, [handleImageFiles]);

  // Insert template variable
  const insertVariable = useCallback((variableName: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      const placeholder = `{{${variableName}}}`;

      // Insert with special formatting for variables
      quill.insertText(range.index, placeholder);
      quill.formatText(range.index, placeholder.length, {
        'background': '#f3f4f6',
        'color': '#3b82f6',
        'bold': true
      });
      quill.setSelection(range.index + placeholder.length, 0);
    }
    setShowVariablePanel(false);
  }, []);

  // Quill modules configuration - use ID selector (React Quill will find it)
  const modules = useMemo(() => {
    const toolbarSelector = `#${toolbarId}`;
    console.log(`[RichTextEditor] Creating modules with toolbar selector: ${toolbarSelector}`);
    return {
      toolbar: {
        container: toolbarSelector,
        handlers: {
          image: imageHandler
        }
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize', 'Toolbar']
      },
      imageDrop: true,
      clipboard: {
        matchVisual: false
      }
    };
  }, [imageHandler, toolbarId]);
  
  // Ensure editor is enabled and editable after mount
  useEffect(() => {
    const enableEditor = () => {
      const quill = quillRef.current?.getEditor();
      const toolbarElement = document.getElementById(toolbarId);
      
      console.log(`[RichTextEditor] Editor initialization check:`, {
        toolbarId,
        toolbarFound: !!toolbarElement,
        quillInitialized: !!quill,
        quillEnabled: quill?.isEnabled(),
        rootElement: quill?.root?.tagName,
        rootContentEditable: quill?.root?.getAttribute('contenteditable'),
        rootStyle: quill?.root ? window.getComputedStyle(quill.root).pointerEvents : null
      });
      
      if (quill) {
        // Ensure editor is enabled
        if (!quill.isEnabled()) {
          console.warn(`[RichTextEditor] Enabling Quill editor...`);
          quill.enable(true);
        }
        
        // Ensure the editor root is editable
        if (quill.root) {
          quill.root.setAttribute('contenteditable', 'true');
          quill.root.style.pointerEvents = 'auto';
          quill.root.style.cursor = 'text';
          quill.root.style.userSelect = 'text';
          
          // Remove any disabled attributes
          quill.root.removeAttribute('disabled');
          quill.root.removeAttribute('readonly');
          
          console.log(`[RichTextEditor] Editor root updated:`, {
            contentEditable: quill.root.getAttribute('contenteditable'),
            pointerEvents: quill.root.style.pointerEvents,
            cursor: quill.root.style.cursor
          });
        }
        
        // Try to focus the editor to test if it's interactive
        if (quill.root && document.activeElement !== quill.root) {
          try {
            quill.root.focus();
            console.log(`[RichTextEditor] Editor focused successfully`);
          } catch (e) {
            console.error(`[RichTextEditor] Failed to focus editor:`, e);
          }
        }
      } else {
        console.warn(`[RichTextEditor] Quill editor not initialized yet`);
      }
    };
    
    // Check after React Quill has initialized (multiple attempts)
    const timeoutId1 = setTimeout(enableEditor, 100);
    const timeoutId2 = setTimeout(enableEditor, 300);
    const timeoutId3 = setTimeout(enableEditor, 500);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, [toolbarId, value]);

  // Quill formats
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'align', 'color', 'background',
    'code-block'
  ];

  // Preview content with sample data
  const getPreviewContent = () => {
    let previewHtml = value;

    // Replace template variables with sample data
    const sampleData: Record<string, string> = {
      client_name: 'John Doe',
      client_email: 'john.doe@example.com',
      client_phone: '(555) 123-4567',
      client_address: '123 Main St, Anytown, ST 12345',
      account_number: 'ACC-123456',
      case_number: 'CASE-789012',
      company_name: 'Regal Credit Services',
      agent_name: 'Sarah Johnson',
      agent_email: 'sarah.johnson@regalcredit.com',
      agent_phone: '(555) 987-6543',
      current_date: new Date().toLocaleDateString(),
      current_time: new Date().toLocaleTimeString(),
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      credit_score: '720',
      payment_amount: '$250.00',
      balance_due: '$1,250.00'
    };

    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      previewHtml = previewHtml.replace(regex, `<span class="bg-blue-100 text-blue-800 px-1 rounded">${value}</span>`);
    });

    return previewHtml;
  };

  // Get preview container class based on mode
  const getPreviewClass = () => {
    const baseClass = 'transition-all duration-300 bg-white border border-gray-200 rounded-lg p-4 mx-auto';
    switch (previewMode) {
      case 'mobile':
        return `${baseClass} max-w-sm`;
      case 'tablet':
        return `${baseClass} max-w-2xl`;
      case 'desktop':
      default:
        return `${baseClass} max-w-4xl`;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Custom Toolbar */}
      <div ref={toolbarRef} id={toolbarId} className="border border-gray-300 dark:border-gray-600 rounded-t-md bg-gray-50 dark:bg-gray-700 p-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Text Formatting */}
          <div className="flex items-center gap-1 border-r border-gray-300 dark:border-gray-600 pr-3">
            <select className="ql-header" defaultValue="">
              <option value="">Normal</option>
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
            </select>
            <select className="ql-font" defaultValue="">
              <option value="">Default</option>
              <option value="serif">Serif</option>
              <option value="monospace">Monospace</option>
            </select>
            <select className="ql-size" defaultValue="">
              <option value="small">Small</option>
              <option value="">Normal</option>
              <option value="large">Large</option>
              <option value="huge">Huge</option>
            </select>
          </div>

          {/* Style Buttons */}
          <div className="flex items-center gap-1 border-r border-gray-300 dark:border-gray-600 pr-3">
            <button className="ql-bold" title="Bold">
              <Bold className="h-4 w-4" />
            </button>
            <button className="ql-italic" title="Italic">
              <Italic className="h-4 w-4" />
            </button>
            <button className="ql-underline" title="Underline">
              <Underline className="h-4 w-4" />
            </button>
            <button className="ql-strike" title="Strikethrough">
              <Type className="h-4 w-4" />
            </button>
          </div>

          {/* Alignment */}
          <div className="flex items-center gap-1 border-r border-gray-300 dark:border-gray-600 pr-3">
            <button className="ql-align" value="" title="Align Left">
              <AlignLeft className="h-4 w-4" />
            </button>
            <button className="ql-align" value="center" title="Align Center">
              <AlignCenter className="h-4 w-4" />
            </button>
            <button className="ql-align" value="right" title="Align Right">
              <AlignRight className="h-4 w-4" />
            </button>
          </div>

          {/* Lists */}
          <div className="flex items-center gap-1 border-r border-gray-300 dark:border-gray-600 pr-3">
            <button className="ql-list" value="ordered" title="Numbered List">
              <ListOrdered className="h-4 w-4" />
            </button>
            <button className="ql-list" value="bullet" title="Bullet List">
              <List className="h-4 w-4" />
            </button>
          </div>

          {/* Insert Options */}
          <div className="flex items-center gap-1 border-r border-gray-300 dark:border-gray-600 pr-3">
            <button className="ql-link" title="Insert Link">
              <Link className="h-4 w-4" />
            </button>
            <button className="ql-image" title="Insert Image">
              <Image className="h-4 w-4" />
            </button>
            <button className="ql-blockquote" title="Quote">
              <Quote className="h-4 w-4" />
            </button>
            <button className="ql-code-block" title="Code Block">
              <Code className="h-4 w-4" />
            </button>
          </div>

          {/* Colors */}
          <div className="flex items-center gap-1 border-r border-gray-300 dark:border-gray-600 pr-3">
            <select className="ql-color" />
            <select className="ql-background" />
          </div>

          {/* Custom Actions */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowVariablePanel(!showVariablePanel)}
              className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
              title="Insert Variable"
            >
              <Plus className="h-3 w-3" />
              Variable
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="px-2 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-1"
              title="Toggle Preview"
            >
              {showPreview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              Preview
            </button>
          </div>
        </div>
      </div>

      {/* Variable Panel */}
      {showVariablePanel && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-b-md shadow-lg max-h-64 overflow-y-auto">
          <div className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Insert Template Variable</h4>
              <button
                onClick={() => setShowVariablePanel(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Group variables by category */}
            {Array.from(new Set(TEMPLATE_VARIABLES.map(v => v.category))).map(category => (
              <div key={category} className="mb-4">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{category}</h5>
                <div className="grid grid-cols-2 gap-2">
                  {TEMPLATE_VARIABLES
                    .filter(v => v.category === category)
                    .map(variable => (
                      <button
                        key={variable.name}
                        onClick={() => insertVariable(variable.name)}
                        className="text-left px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                      >
                        <div className="font-mono text-xs text-blue-600 dark:text-blue-400">
                          {`{{${variable.name}}}`}
                        </div>
                        <div className="truncate">{variable.label}</div>
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Editor */}
        <div className={`flex-1 ${showPreview ? 'lg:w-1/2' : 'w-full'}`}>
          <div
            className={`relative ${isDragging ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragging && (
              <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-400 rounded-md flex items-center justify-center z-10">
                <div className="text-center">
                  <Upload className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-blue-600 dark:text-blue-400 font-medium">Drop images here to upload</p>
                </div>
              </div>
            )}

            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={value}
              onChange={(content) => {
                console.log(`[RichTextEditor] onChange called with content length:`, content?.length || 0);
                onChange(content);
              }}
              placeholder={placeholder}
              modules={modules}
              formats={formats}
              readOnly={false}
              bounds="self"
              style={{
                height: `${height}px`,
                marginBottom: '42px'
              }}
              className="bg-white dark:bg-gray-800 border-0 rounded-b-md"
            />

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />

            {/* Upload indicator */}
            {uploadingImages.size > 0 && (
              <div className="absolute bottom-2 right-2 bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                Uploading {uploadingImages.size} image(s)...
              </div>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        {showPreview && (
          <div className="flex-1 lg:w-1/2">
            <div className="border border-gray-300 dark:border-gray-600 rounded-md">
              {/* Preview Header */}
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-300 dark:border-gray-600 flex items-center justify-between rounded-t-md">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Preview</h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewMode('desktop')}
                    className={`p-1 rounded ${previewMode === 'desktop' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}`}
                    title="Desktop View"
                  >
                    <Monitor className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPreviewMode('tablet')}
                    className={`p-1 rounded ${previewMode === 'tablet' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}`}
                    title="Tablet View"
                  >
                    <Tablet className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPreviewMode('mobile')}
                    className={`p-1 rounded ${previewMode === 'mobile' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}`}
                    title="Mobile View"
                  >
                    <Smartphone className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Preview Content */}
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-b-md" style={{ height: `${height}px`, overflowY: 'auto' }}>
                <div className={getPreviewClass()}>
                  <div
                    className="prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        <p>
          Drag and drop images directly into the editor, or use the image button in the toolbar.
          Click "Variable" to insert template placeholders like {`{{client_name}}`}.
        </p>
      </div>
    </div>
  );
};

export default RichTextEditor;